import { _IdGenerator } from '@angular/cdk/a11y';
import { AutofillMonitor } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxIconButton } from '../icon-button/icon-button';
import { HX_FORM_FIELD_CONTROL, HxFormFieldControl } from '../form-group/form-field-control';
import {
  DICTATION_ENABLED_CHANGED_EVENT,
  DictationState,
  HxDictationController,
  isDictationFeatureEnabled,
} from './dictation';

@Component({
  selector: 'hx-textarea',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, HxIconButton],
  templateUrl: './textarea.html',
  styleUrls: ['./textarea.css', '../../styles/form-control-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: HX_FORM_FIELD_CONTROL, useExisting: HxTextarea }],
})
// @ts-expect-error - Property conflicts between HxBaseControl input signals and HxFormFieldControl readonly properties
export class HxTextarea
  extends HxBaseControl
  implements ControlValueAccessor, HxFormFieldControl<string>, OnDestroy, AfterViewInit
{
  private readonly _autofillMonitor = inject(AutofillMonitor);
  private readonly _idGenerator = inject(_IdGenerator);
  readonly ngControl = inject(NgControl, { optional: true, self: true });

  private readonly _textarea = viewChild.required<ElementRef<HTMLTextAreaElement>>('textareaElement');

  // --- Component Inputs ---

  /** Component size */
  readonly size = input<'sm' | 'md' | 'lg' | 'xl'>('md');

  /** Visual variant */
  readonly variant = input<'fill' | 'outline'>('fill');

  /** Number of visible text rows */
  readonly rows = input<number | undefined>(undefined);

  /** Number of visible text columns */
  readonly cols = input<number | undefined>(undefined);

  /** Native `maxlength` attribute, hard-stops typing beyond the limit (validation still relies on ngControl). */
  readonly maxlength = input<number | undefined>(undefined);

  /**
   * Controls whether the dictation (speech-to-text) microphone icon may be shown. Defaults to `undefined`, meaning the
   * component defers to the `dictationEnabled` local-storage feature flag (see `DICTATION_ENABLED_STORAGE_KEY`). Pass
   * `false` to always hide the icon for this instance regardless of the feature flag; pass `true` to force it on (still
   * requires browser support). Existing consumers require no changes to keep current behavior.
   */
  readonly dictationEnabled = input<boolean | undefined>(undefined);

  /** Whether the current browser supports the Web Speech API used for dictation. */
  protected readonly _dictationSupported = HxDictationController.isSupported();

  /** Live-updating local-storage dictation feature flag (kept in sync via storage/custom events). */
  private readonly _dictationFeatureFlag = signal(isDictationFeatureEnabled());

  /** Whether the dictation microphone icon should be rendered. */
  readonly showDictation = computed(
    () => (this.dictationEnabled() ?? this._dictationFeatureFlag()) && this._dictationSupported
  );

  /** Current dictation lifecycle state: idle, listening, or error. */
  readonly dictationState = signal<DictationState>('idle');

  /** Accessible label/tooltip for the dictation button, reflecting the current state. */
  protected readonly dictationButtonLabel = computed(() =>
    this.dictationState() === 'listening' ? 'Stop dictation' : 'Start dictation'
  );

  private _dictationController: HxDictationController | null = null;

  /** Textarea value captured the moment the current dictation session started. */
  private _dictationBaseValue = '';

  /** Finalized transcript text committed so far during the current dictation session. */
  private _dictationCommittedText = '';

  private readonly _refreshDictationFeatureFlag = (): void => {
    this._dictationFeatureFlag.set(isDictationFeatureEnabled());
    this._changeDetectorRef.markForCheck();
  };

  // --- HxFormFieldControl Implementation ---

  /** Unique identifier for this textarea */
  private readonly _formFieldId = this._idGenerator.getId('hx-textarea-');

  /** Gets the id value for use in template and interface methods */
  get idValue(): string {
    return this.getIdValue() || this._formFieldId;
  }

  /** Stream that emits whenever the state of the control changes */
  readonly stateChanges = new Subject<void>();

  /** The type of control (for HxFormGroup) */
  readonly formFieldControlType = 'hx-textarea';

  /** Whether the textarea is currently focused */
  focused = false;

  /** Whether the textarea has been autofilled */
  autofilled = false;

  /** List of element IDs that describe this control (set by HxFormGroup) */
  private _describedByIds: string[] = [];

  // --- ControlValueAccessor ---
  // Note: Using base class methods for onChange/onTouched via notifyChange() and markTouched()

  // --- HxFormFieldControl Properties (as getters) ---

  /** Gets the current value from the native textarea */
  get value(): string {
    return this._textarea().nativeElement.value || '';
  }

  /** Sets the current value on the native textarea */
  set value(val: string) {
    const newValue = val ?? '';
    if (this._textarea().nativeElement.value !== newValue) {
      this._textarea().nativeElement.value = newValue;
      this.stateChanges.next();
    }
  }

  /** Gets the placeholder value */
  get placeholderValue(): string {
    return this.placeholder() || this._textarea().nativeElement.placeholder || '';
  }

  /** Gets the disabled state for HxFormFieldControl interface */
  get disabledValue(): boolean {
    return this.disabledState();
  }

  /** Gets the required state for HxFormFieldControl interface */
  get requiredValue(): boolean {
    return this.required();
  }

  /** Whether the textarea is empty */
  get empty(): boolean {
    return !this.value || this.value.length === 0;
  }

  /** Whether the control is in an error state */
  get errorState(): boolean {
    // Check NgControl validation state
    const control = this.ngControl?.control;
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /** Whether the label should float */
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  constructor() {
    super();
    // Register this component as the CVA implementation
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    // Monitor autofill state on the actual textarea element
    this._autofillMonitor.monitor(this._textarea()).subscribe((event) => {
      this.autofilled = event.isAutofilled;
      this.stateChanges.next();
    });

    if (typeof window !== 'undefined') {
      window.addEventListener(DICTATION_ENABLED_CHANGED_EVENT, this._refreshDictationFeatureFlag);
      window.addEventListener('storage', this._refreshDictationFeatureFlag);
    }
  }

  ngOnDestroy(): void {
    this._autofillMonitor.stopMonitoring(this._textarea());
    this.stateChanges.complete();

    if (typeof window !== 'undefined') {
      window.removeEventListener(DICTATION_ENABLED_CHANGED_EVENT, this._refreshDictationFeatureFlag);
      window.removeEventListener('storage', this._refreshDictationFeatureFlag);
    }

    this._dictationController?.dispose();
    this._dictationController = null;
  }

  // HxFormFieldControl methods
  setDescribedByIds(ids: string[]): void {
    this._describedByIds = ids;
    const element = this._textarea().nativeElement;
    if (ids.length > 0) {
      element.setAttribute('aria-describedby', ids.join(' '));
    } else {
      element.removeAttribute('aria-describedby');
    }
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'textarea') {
      this._textarea().nativeElement.focus();
    }
  }

  focus(): void {
    this._textarea().nativeElement.focus();
  }

  // Event handlers (moved from @HostListener)
  _onFocus(): void {
    this.focused = true;
    this.stateChanges.next();
  }

  _onBlur(): void {
    this.focused = false;
    this.stateChanges.next();
    this.markTouched();
  }

  _onInput(): void {
    const newValue = this._textarea().nativeElement.value || '';
    this.notifyChange(newValue);
    this.stateChanges.next();

    // Manual edits (typing/deleting) while dictation is listening must not be clobbered by the next
    // interim/final speech event, which otherwise recomposes from the stale base captured at session start.
    if (this.dictationState() === 'listening') {
      this._dictationBaseValue = newValue;
      this._dictationCommittedText = '';
    }
  }

  /** Pre-warms the microphone pipeline before the user actually clicks, reducing recognition startup latency. */
  _onDictationButtonWarmUp(): void {
    HxDictationController.warmUp();
  }

  /** Handles clicks on the dictation microphone button: starts or stops the active recognition session. */
  _onDictationButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabledState() || this.readonly()) {
      return;
    }

    if (this.dictationState() === 'listening') {
      this._dictationController?.stop();
      return;
    }

    this._dictationBaseValue = this._textarea().nativeElement.value || '';
    this._dictationCommittedText = '';

    if (!this._dictationController) {
      this._dictationController = new HxDictationController({
        onInterimResult: (transcript) => this._previewDictatedText(transcript),
        onFinalResult: (transcript) => this._commitDictatedText(transcript),
        onStateChange: (state) => {
          this.dictationState.set(state);
          this._changeDetectorRef.markForCheck();
        },
        onError: () => {
          // Errors (permission denied/dismissed, no speech, network, unsupported, etc.) simply return the
          // control to idle/error state via onStateChange — the textarea's existing content and normal
          // behavior are left completely untouched.
        },
      });
    }

    this._dictationController.start();
  }

  /** Builds `base + committed + interim`, skipping empty parts and joining with single spaces. */
  private _composeDictationValue(interim: string): string {
    return [this._dictationBaseValue.replace(/\s+$/, ''), this._dictationCommittedText, interim]
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .join(' ');
  }

  /** Live preview: shows in-progress (not-yet-final) speech immediately, without committing it. */
  private _previewDictatedText(interimTranscript: string): void {
    this._applyDictatedValue(this._composeDictationValue(interimTranscript), true);
  }

  /** Commits finalized speech permanently so it is never overwritten/duplicated by later interim previews. */
  private _commitDictatedText(finalTranscript: string): void {
    const trimmed = finalTranscript.trim();
    if (!trimmed) {
      return;
    }
    this._dictationCommittedText = [this._dictationCommittedText, trimmed].filter(Boolean).join(' ');
    this._applyDictatedValue(this._composeDictationValue('') + ' ', true);
  }

  /** Writes a dictation-driven value into the textarea through the same change pipeline as manual typing. */
  private _applyDictatedValue(newValue: string, notify: boolean): void {
    const element = this._textarea().nativeElement;
    element.value = newValue;
    element.setSelectionRange(newValue.length, newValue.length);

    if (notify) {
      this.notifyChange(newValue);
      this.stateChanges.next();
    }
    this._changeDetectorRef.markForCheck();
  }

  // --- HxBaseControl Overrides ---

  /** Sets the value from form model (ControlValueAccessor) */
  protected _setValue(value: string): void {
    this.value = value;
  }

  /** Validates the textarea value */
  protected _validate(_control: AbstractControl): ValidationErrors | null {
    // Validation is handled by HxFormFieldControl errorState
    return null;
  }
}
